import { Link } from "react-router";
import { ArrowRight } from "lucide-react";
import {
  renderMergeFields,
  type MergeFieldContext,
  type PortalContentResolution,
} from "@colab/shared";

type WelcomeBannerProps = {
  resolution: PortalContentResolution<"welcome">;
  mergeContext: MergeFieldContext;
  fallbackName?: string | null;
};

// Welcome banner at the top of the partner dashboard. Reads from the resolved
// "welcome" portal content (most-specific scope picked by the api) and
// substitutes merge tokens at view time. Falls back to a generic greeting if
// no row exists or the resolved row failed shape validation.
export function WelcomeBanner({ resolution, mergeContext, fallbackName }: WelcomeBannerProps) {
  const row = resolution.resolved;

  const title = row
    ? renderMergeFields(row.content.title, mergeContext)
    : `Welcome back${fallbackName ? `, ${fallbackName}` : ""} 👋`;

  const body = row
    ? renderMergeFields(row.content.body, mergeContext)
    : "Your partner hub — register deals, browse resources, and track your progress.";

  const coverImageUrl = row?.content.cover_image_url ?? null;
  const primaryCta = row?.content.primary_cta ?? null;
  const secondaryCta = row?.content.secondary_cta ?? null;

  return (
    <section className="relative mb-7 overflow-hidden rounded-2xl border bg-gradient-to-br from-accent/10 via-background to-background p-6 sm:p-8">
      {coverImageUrl ? (
        <div className="absolute inset-y-0 right-0 hidden w-1/3 opacity-30 sm:block">
          <img
            src={coverImageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover object-center"
          />
        </div>
      ) : null}

      <div className="relative">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          Welcome back
        </p>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight sm:text-4xl">
          {title}
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">{body}</p>

        {(primaryCta || secondaryCta) && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {primaryCta ? (
              <Link
                to={primaryCta.href}
                className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-3.5 py-2 text-sm font-medium text-background transition hover:bg-foreground/90"
              >
                {primaryCta.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : null}
            {secondaryCta ? (
              <Link
                to={secondaryCta.href}
                className="inline-flex items-center gap-1.5 rounded-md border bg-background px-3.5 py-2 text-sm font-medium text-foreground transition hover:border-accent hover:text-accent"
              >
                {secondaryCta.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
